import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from './BusinessContext';
import { fetchAndStoreMessages, validateTwilioSettingsForBusiness } from '../lib/twilio';

interface GlobalMessagePollerContextType {
  isPolling: boolean;
  lastPolled: Date | null;
  twilioEnabled: boolean;
  gmailEnabled: boolean;
  services: {
    twilio: {
      enabled: boolean;
      lastPolled: Date | null;
      isPolling: boolean;
    };
    gmail: {
      enabled: boolean;
      lastPolled: Date | null;
      isPolling: boolean;
    };
  };
}

// Create context with default values
const GlobalMessagePollerContext = createContext<GlobalMessagePollerContextType>({
  isPolling: false,
  lastPolled: null,
  twilioEnabled: false,
  gmailEnabled: false,
  services: {
    twilio: {
      enabled: false,
      lastPolled: null,
      isPolling: false
    },
    gmail: {
      enabled: false,
      lastPolled: null,
      isPolling: false
    }
  }
});

// Custom hook to use the context
export const useGlobalMessagePoller = () => useContext(GlobalMessagePollerContext);

// For backward compatibility
export const useTwilioMessagePoller = () => {
  const context = useGlobalMessagePoller();
  return {
    isPolling: context.services.twilio.isPolling,
    lastPolled: context.services.twilio.lastPolled,
    twilioEnabled: context.twilioEnabled
  };
};

interface GlobalMessagePollerProviderProps {
  children: React.ReactNode;
  pollingInterval?: number; // in milliseconds
}

export const GlobalMessagePollerProvider: React.FC<GlobalMessagePollerProviderProps> = ({ 
  children, 
  pollingInterval = 30000 // Default to polling every 30 seconds (less aggressive than 5s)
}) => {
  const { selectedBusiness } = useBusiness();
  const [isPolling, setIsPolling] = useState(false);
  const [lastPolled, setLastPolled] = useState<Date | null>(null);
  
  // Service-specific states
  const [twilioEnabled, setTwilioEnabled] = useState(false);
  const [gmailEnabled, setGmailEnabled] = useState(false);
  
  const [twilioSettings, setTwilioSettings] = useState<{
    accountSid: string;
    authToken: string;
    phoneNumber: string;
  } | null>(null);
  
  const [services, setServices] = useState({
    twilio: {
      enabled: false,
      lastPolled: null as Date | null,
      isPolling: false
    },
    gmail: {
      enabled: false,
      lastPolled: null as Date | null,
      isPolling: false
    }
  });
  
  const pollerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to fetch Twilio settings
  const fetchTwilioSettings = async () => {
    if (!selectedBusiness?.id) return false;
    
    try {
      console.log('Global poller: Checking Twilio settings');
      const validation = await validateTwilioSettingsForBusiness(selectedBusiness.id);
      
      if (validation.isValid && validation.settings) {
        setTwilioEnabled(true);
        setTwilioSettings({
          accountSid: validation.settings.twilio_account_sid || '',
          authToken: validation.settings.twilio_auth_token || '',
          phoneNumber: validation.settings.VITE_TWILIO_PHONE_NUMBER || ''
        });
        setServices(prev => ({
          ...prev,
          twilio: { ...prev.twilio, enabled: true }
        }));
        console.log('Global poller: Twilio is enabled and configured');
        return true;
      } else {
        setTwilioEnabled(false);
        setTwilioSettings(null);
        setServices(prev => ({
          ...prev,
          twilio: { ...prev.twilio, enabled: false }
        }));
        console.log('Global poller: Twilio is not properly configured:', validation.error);
        return false;
      }
    } catch (error) {
      console.error('Global poller: Error fetching Twilio settings:', error);
      setTwilioEnabled(false);
      setTwilioSettings(null);
      setServices(prev => ({
        ...prev,
        twilio: { ...prev.twilio, enabled: false }
      }));
      return false;
    }
  };

  // Function to check Gmail connection
  const checkGmailConnection = async () => {
    if (!selectedBusiness?.id || !supabase) return false;
    
    try {
      console.log('Global poller: Checking Gmail connection');
      
      const { data: connection, error } = await supabase
        .from('gmail_connections')
        .select('id, email, is_active, token_expires_at')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .single();

      if (error || !connection) {
        setGmailEnabled(false);
        setServices(prev => ({
          ...prev,
          gmail: { ...prev.gmail, enabled: false }
        }));
        console.log('Global poller: No active Gmail connection found');
        return false;
      }

      // Check if token is not expired (give 5 minute buffer)
      const tokenExpiry = new Date(connection.token_expires_at);
      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
      
      if (tokenExpiry < fiveMinutesFromNow) {
        console.log('Global poller: Gmail token expired or expiring soon');
        // Don't disable completely, let the edge function handle refresh
      }

      setGmailEnabled(true);
      setServices(prev => ({
        ...prev,
        gmail: { ...prev.gmail, enabled: true }
      }));
      console.log('Global poller: Gmail is enabled for:', connection.email);
      return true;
    } catch (error) {
      console.error('Global poller: Error checking Gmail connection:', error);
      setGmailEnabled(false);
      setServices(prev => ({
        ...prev,
        gmail: { ...prev.gmail, enabled: false }
      }));
      return false;
    }
  };

  // Function to fetch emails via edge function
  const fetchEmails = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      console.log('Global poller: Fetching emails via edge function');
      setServices(prev => ({
        ...prev,
        gmail: { ...prev.gmail, isPolling: true }
      }));

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gmail-fetch-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          businessId: selectedBusiness.id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Gmail fetch failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('Global poller: Gmail fetch result:', result);
      
      setServices(prev => ({
        ...prev,
        gmail: { ...prev.gmail, lastPolled: new Date() }
      }));

    } catch (error) {
      console.error('Global poller: Error fetching emails:', error);
      // Don't disable Gmail on error, just log and continue
    } finally {
      setServices(prev => ({
        ...prev,
        gmail: { ...prev.gmail, isPolling: false }
      }));
    }
  };

  // Function to poll for Twilio messages
  const pollTwilioMessages = async () => {
    if (!selectedBusiness?.id || !twilioEnabled || !twilioSettings) {
      return;
    }
    
    try {
      console.log('Global poller: Polling for Twilio messages');
      setServices(prev => ({
        ...prev,
        twilio: { ...prev.twilio, isPolling: true }
      }));
      
      await fetchAndStoreMessages({
        businessId: selectedBusiness.id,
        accountSid: twilioSettings.accountSid,
        authToken: twilioSettings.authToken,
        phoneNumber: twilioSettings.phoneNumber
      });
      
      setServices(prev => ({
        ...prev,
        twilio: { ...prev.twilio, lastPolled: new Date() }
      }));
      console.log('Global poller: Successfully polled Twilio messages');
    } catch (error) {
      console.error('Global poller: Error polling Twilio messages:', error);
    } finally {
      setServices(prev => ({
        ...prev,
        twilio: { ...prev.twilio, isPolling: false }
      }));
    }
  };

  // Main polling function that handles both services
  const pollAllMessages = async () => {
    if (!selectedBusiness?.id) {
      console.log('Global poller: Skipping poll (no business selected)');
      return;
    }
    
    setIsPolling(true);
    
    try {
      // Run both polling operations in parallel for efficiency
      const promises = [];
      
      if (twilioEnabled && twilioSettings) {
        promises.push(pollTwilioMessages());
      }
      
      if (gmailEnabled) {
        promises.push(fetchEmails());
      }
      
      if (promises.length > 0) {
        await Promise.allSettled(promises);
        setLastPolled(new Date());
        console.log('Global poller: Completed polling cycle at', new Date().toISOString());
      } else {
        console.log('Global poller: No services enabled for polling');
      }
      
    } catch (error) {
      console.error('Global poller: Error in polling cycle:', error);
    } finally {
      setIsPolling(false);
    }
  };

  // Set up initial polling and handle business changes
  useEffect(() => {
    // Clean up existing poller
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
    
    if (!selectedBusiness?.id) {
      setTwilioEnabled(false);
      setGmailEnabled(false);
      setTwilioSettings(null);
      return;
    }
    
    // Check both services and start polling if either is available
    const initializeServices = async () => {
      console.log('Global poller: Initializing services for business:', selectedBusiness.id);
      
      const [twilioReady, gmailReady] = await Promise.all([
        fetchTwilioSettings(),
        checkGmailConnection()
      ]);
      
      if (twilioReady || gmailReady) {
        console.log(`Global poller: Services ready - Twilio: ${twilioReady}, Gmail: ${gmailReady}`);
        
        // Poll immediately
        pollAllMessages();
        
        // Set up interval for regular polling
        pollerRef.current = setInterval(pollAllMessages, pollingInterval);
        
        console.log(`Global poller: Started polling every ${pollingInterval / 1000} seconds`);
      } else {
        console.log('Global poller: No services are configured and ready');
      }
    };
    
    initializeServices();
    
    // Clean up on unmount or business change
    return () => {
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
    };
  }, [selectedBusiness?.id]);

  // Update polling when service settings change
  useEffect(() => {
    const hasEnabledServices = twilioEnabled || gmailEnabled;
    
    if (hasEnabledServices && selectedBusiness?.id) {
      // Clean up existing poller
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
      }
      
      // Start a new polling interval
      pollerRef.current = setInterval(pollAllMessages, pollingInterval);
      console.log(`Global poller: Updated polling with new settings every ${pollingInterval / 1000} seconds`);
    } else if (pollerRef.current) {
      // If no services enabled but we have an active poller, clean it up
      clearInterval(pollerRef.current);
      pollerRef.current = null;
      console.log('Global poller: Stopped polling due to no enabled services');
    }
  }, [twilioEnabled, gmailEnabled, twilioSettings, pollingInterval]);

  // Set up a background task to periodically re-check service settings
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    
    // Check settings every 10 minutes to handle cases where they change elsewhere
    const settingsCheckInterval = setInterval(async () => {
      console.log('Global poller: Periodic service settings check');
      await Promise.all([
        fetchTwilioSettings(),
        checkGmailConnection()
      ]);
    }, 10 * 60 * 1000);
    
    return () => {
      clearInterval(settingsCheckInterval);
    };
  }, [selectedBusiness?.id]);

  return (
    <GlobalMessagePollerContext.Provider value={{ 
      isPolling, 
      lastPolled, 
      twilioEnabled, 
      gmailEnabled,
      services
    }}>
      {children}
    </GlobalMessagePollerContext.Provider>
  );
};

// Export the provider with the new name but keep old name for compatibility
export const TwilioMessagePollerProvider = GlobalMessagePollerProvider; 